<template>
  <section id="reportes-resenas" class="review-reports dashboard-section">
    <div class="review-reports-header">
      <div>
        <h3>Reportes de reseñas</h3>
        <p>Reportes enviados por profesionales de cualquier plan. Revisa el caso antes de ocultar una reseña.</p>
      </div>
      <div class="review-reports-actions">
        <label for="review-report-status">Estado</label>
        <select id="review-report-status" v-model="status" :disabled="loading || saving !== null" @change="page = 1; load()">
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
          <option value="all">Todos</option>
        </select>
        <button type="button" :disabled="loading || saving !== null" @click="load">Actualizar</button>
      </div>
    </div>
    <p v-if="error" role="alert" class="review-reports-error">{{ error }}</p>
    <p v-if="loading" role="status">Cargando reportes…</p>
    <p v-else-if="!reports.length">No hay reportes en esta página.</p>
    <article v-for="item in reports" :key="item.id" class="review-report">
      <div class="review-reports-header">
        <strong>Reseña #{{ item.id }} · {{ item.profesional.nombre || 'Profesional' }} (#{{ item.profesional.id }})</strong>
        <span>{{ statusLabel(item.reporte.estado) }} · {{ formatDate(item.reporte.fecha_creado) }}</span>
      </div>
      <p><strong>Cliente:</strong> {{ item.cliente }} · {{ item.calificacion }}/5</p>
      <p class="review-report-text">{{ item.comentario || 'Sin comentario.' }}</p>
      <div v-if="item.original && (item.original.comentario !== item.comentario || item.original.calificacion !== item.calificacion)" class="review-report-original">
        <strong>Reseña al momento del reporte ({{ item.original.calificacion }}/5)</strong>
        <p class="review-report-text">{{ item.original.comentario || 'Sin comentario.' }}</p>
      </div>
      <p><strong>Motivo:</strong> {{ item.reporte.motivo_label }}</p>
      <p class="review-report-text">{{ item.reporte.detalle }}</p>
      <p v-if="item.oculto">Esta reseña ya está oculta.</p>
      <template v-if="item.reporte.estado === 'pendiente'">
        <label :for="'review-resolution-' + item.id">Resolución para el profesional</label>
        <textarea :id="'review-resolution-' + item.id" v-model="notes[item.id]" maxlength="2000" rows="3" :disabled="saving !== null" />
        <div class="review-reports-actions">
          <button type="button" :disabled="loading || saving !== null" @click="resolve(item, 'aprobado')">Aprobar y ocultar reseña</button>
          <button type="button" :disabled="loading || saving !== null" @click="resolve(item, 'rechazado')">Rechazar reporte</button>
          <span v-if="saving === item.id" role="status">Guardando…</span>
        </div>
      </template>
      <p v-else class="review-report-text"><strong>Resolución:</strong> {{ item.reporte.resolucion }}</p>
    </article>
    <div class="review-reports-actions">
      <button type="button" :disabled="page <= 1 || loading || saving !== null" @click="page--; load()">Anterior</button>
      <span>Página {{ page }}</span>
      <button type="button" :disabled="!hasMore || loading || saving !== null" @click="page++; load()">Siguiente</button>
    </div>
  </section>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReviewReports',
  data: () => ({ reports: [], status: 'pendiente', page: 1, hasMore: false, loading: false, saving: null, error: '', notes: {} }),
  mounted() { this.load(); },
  methods: {
    url() { return `${window.hostname.replace(/\/$/, '')}/api/superadmin/review-reports`; },
    statusLabel(status) { return { pendiente: 'Pendiente', aprobado: 'Aprobado', rechazado: 'Rechazado' }[status] || status; },
    formatDate(value) { return value ? new Date(value).toLocaleString('es-CL') : ''; },
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(this.url(), { params: { estado: this.status, page: this.page } });
        this.reports = data.reports;
        this.hasMore = data.has_more;
      } catch (error) {
        this.error = error.response?.data?.message || 'No pudimos cargar los reportes. Intenta actualizar.';
      } finally { this.loading = false; }
    },
    async resolve(item, estado) {
      if (this.saving !== null || this.loading) return;
      const resolucion = (this.notes[item.id] || '').trim();
      if (!resolucion || [...resolucion].length > 2000) {
        this.error = 'Escribe una resolución de entre 1 y 2000 caracteres.';
        return;
      }
      this.saving = item.id;
      this.error = '';
      try {
        await axios.put(`${this.url()}/${item.id}`, { estado, resolucion });
        this.$delete(this.notes, item.id);
        await this.load();
      } catch (error) {
        if (error.response?.status === 409) await this.load();
        this.error = error.response?.data?.message || 'No pudimos guardar la resolución. Intenta nuevamente.';
      } finally { this.saving = null; }
    },
  },
};
</script>

<style scoped>
.review-reports { grid-column: 1 / -1; padding: 20px; }
.review-reports h3 { margin: 0 0 8px; }
.review-reports-header, .review-reports-actions { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.review-reports-actions { justify-content: flex-start; }
.review-reports-header p { margin-bottom: 12px; }
.review-report { padding: 18px 0; margin: 8px 0; border-top: 1px solid #dbe3eb; }
.review-report-text { white-space: pre-wrap; overflow-wrap: anywhere; }
.review-report-original { background: #f1f5f9; padding: 12px; border-radius: 8px; }
.review-reports textarea { display: block; width: 100%; box-sizing: border-box; margin: 8px 0 12px; padding: 10px; }
.review-reports button, .review-reports select { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 6px; background: white; }
.review-reports button:disabled { opacity: 0.5; }
.review-reports-error { color: #b91c1c; }
</style>
